import { isNotNil } from "client/Misc/utils";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import { useCallback, useEffect, useState } from "react";
import {
	attachToPopState,
	detachFromPopState,
	getState,
	PopStateHandler,
	pushState,
	replaceState
} from "client_legacy/ndaw/browserHistoryUtils";
import { never } from "client_legacy/ndaw/errors";

export type ChangeDocumentHistoryState = (
	state: IStoreHistoryState,
	url: string
) => void;

export function useDocumentsHistoryState()
	: [historyState: IStoreHistoryState | undefined, setHistoryState: ChangeDocumentHistoryState] {

	const [historyState, setHistoryState] = useState<IStoreHistoryState | undefined>(undefined);

	const historyChangedHandler = useCallback(
		(state: unknown): void => {
			if (!isStoreHistoryState(state)) {
				return;
			}

			setHistoryState(state);
		},
		[]
	);

	const originalChangeHistoryState: ChangeHistoryState = useHistoryState(historyChangedHandler);

	const changeHistoryState = useCallback(
		(
			state: IStoreHistoryState,
			url: string
		): void => {

			setHistoryState(state);

			const kind: HistoryChangeKind = window.location.href === url
				? "replace"
				: "push";

			originalChangeHistoryState(
				kind,
				state,
				url
			);
		},
		[originalChangeHistoryState]
	);

	return [
		historyState,
		changeHistoryState
	];
};

function isStoreHistoryState(value: unknown): value is IStoreHistoryState {
	return isNotNil(
		(value as IStoreHistoryState | nil)?.store
	);
}

export type HistoryChangeKind = "push" | "replace";

export type ChangeHistoryState = (
	kind: HistoryChangeKind,
	state: any,
	url: string
) => void;

export function useHistoryState(
	onChanged: (state: unknown) => void
): ChangeHistoryState {

	useEffect(
		(): (() => void) => {
			const handler: PopStateHandler = (): void => {
				try {
					const state: unknown = getState();
					onChanged(state);
				}
				catch (e) {
					console.error(e);
				}
			};

			const subscribedHandler: PopStateHandler = attachToPopState(handler);

			return (): void => detachFromPopState(subscribedHandler);
		},
		[onChanged]
	);

	return useCallback(
		(
			kind: "push" | "replace",
			state: any,
			url: string
		): void => {
			try {
				switch (kind) {
					case "push":
						pushState(state, "", url);
						break;
					case "replace":
						replaceState(state, "", url);
						break;
					default:
						never(kind);
				}
			}
			catch (err) {
				console.error(err);
			}
		},
		[]
	);
};
